import { useState } from "react";
import { Link, useParams } from "react-router";
import type {
  ApplyKit as ApplyKitData,
  ApplyKitField,
  ApplyKitFieldStatus,
} from "@resume-cloudflare/shared";
import { CheckCircle2, ClipboardCheck, Copy, ExternalLink, FileText } from "lucide-react";
import { useApplyKit, useSubmitApplication } from "../lib/apply";
import { useUpdateApplicationStatus } from "../lib/applications";
import { Button } from "../components/ui/Button";
import { Card, CardBody, CardHeader } from "../components/ui/Card";
import { Dialog } from "../components/ui/Dialog";
import { EmptyState } from "../components/ui/EmptyState";
import { Pill, type PillTone } from "../components/ui/Pill";
import { Skeleton } from "../components/ui/Skeleton";

// Read-only copy-paste view of an application's form. The drafting/editing
// happens in ApplicationAssistant (/applications/:id/apply); this page is
// where you go once answers are ready and you just need to paste each section
// into the real form by hand. The system never fills or submits the form.

const STATUS_TONE: Record<ApplyKitFieldStatus, PillTone> = {
  ready: "success",
  needs_answer: "warning",
  needs_profile: "warning",
  manual: "neutral",
};

const STATUS_LABEL: Record<ApplyKitFieldStatus, string> = {
  ready: "Ready",
  needs_answer: "Needs answer",
  needs_profile: "Needs profile",
  manual: "Manual",
};

function CopyButton({ value, label = "Copy" }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size="sm"
      leadingIcon={
        copied ? <ClipboardCheck size={12} aria-hidden /> : <Copy size={12} aria-hidden />
      }
      onClick={async () => {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
    >
      {copied ? "Copied" : label}
    </Button>
  );
}

function FieldRow({ field }: { field: ApplyKitField }) {
  return (
    <div className="space-y-1 border-t border-neutral-800 py-3 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm text-neutral-200">
          {field.label}
          {field.required ? <span className="ml-1 text-danger-fg">*</span> : null}
        </span>
        <div className="flex items-center gap-2">
          <Pill tone={STATUS_TONE[field.status]}>{STATUS_LABEL[field.status]}</Pill>
          {field.status === "ready" && field.value ? (
            <CopyButton value={field.value} />
          ) : null}
        </div>
      </div>
      {field.status === "ready" && field.value ? (
        <code className="block whitespace-pre-wrap break-words rounded bg-neutral-900 px-2 py-1 text-xs text-neutral-200">
          {field.value}
        </code>
      ) : field.status === "needs_answer" ? (
        <p className="text-xs text-neutral-500">
          Draft this in the{" "}
          <Link to="../apply" relative="path" className="underline-offset-4 hover:underline">
            assistant
          </Link>
          .
        </p>
      ) : field.status === "needs_profile" ? (
        <p className="text-xs text-neutral-500">
          Add this to your{" "}
          <Link to="/profile" className="underline-offset-4 hover:underline">
            profile
          </Link>
          .
        </p>
      ) : (
        <p className="text-xs text-neutral-500">Complete in browser (upload / checkbox / captcha).</p>
      )}
    </div>
  );
}

function Sections({ kit }: { kit: ApplyKitData }) {
  const byName = new Map(kit.fields.map((f) => [f.name, f]));
  return (
    <div className="space-y-4">
      {kit.sections.map((section) => (
        <Card key={section.id}>
          <CardHeader title={section.label} />
          <CardBody>
            {section.field_names
              .map((n) => byName.get(n))
              .filter((f): f is ApplyKitField => Boolean(f))
              .map((f) => (
                <FieldRow key={f.name} field={f} />
              ))}
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

// The accept step is irreversible (essays are snapshotted into the voice
// corpus), so it sits behind an explicit confirm. Only offered before the
// tracker says applied — after that the kanban owns status transitions.
function MarkSubmitted({ kit }: { kit: ApplyKitData }) {
  const [open, setOpen] = useState(false);
  const submit = useSubmitApplication(kit.application.id);
  const updateStatus = useUpdateApplicationStatus();
  if (["applied", "interviewing", "offer"].includes(kit.application.status)) return null;
  const pending = submit.isPending || updateStatus.isPending;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        size="sm"
        leadingIcon={<CheckCircle2 size={12} aria-hidden />}
        onClick={() => setOpen(true)}
      >
        Mark submitted
      </Button>
      <Dialog.Content
        title="Mark this application as submitted?"
        description="Only do this after you've submitted the real form yourself. Accepted answers are snapshotted into your voice corpus and can't be un-accepted, and the tracker moves to applied."
        footer={
          <>
            <Button variant="ghost" size="sm" disabled={pending} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={pending}
              onClick={async () => {
                try {
                  await submit.mutateAsync();
                  await updateStatus.mutateAsync({
                    id: kit.application.id,
                    status: "applied",
                    reason: "Submitted by hand via apply kit",
                  });
                } finally {
                  setOpen(false);
                }
              }}
            >
              {pending ? "Marking…" : "Yes, I submitted it"}
            </Button>
          </>
        }
      >
        <p className="text-sm text-neutral-300">
          {kit.completion.ready}/{kit.completion.total} fields were ready
          {kit.completion.required_missing > 0
            ? `; ${kit.completion.required_missing} required field${kit.completion.required_missing === 1 ? " was" : "s were"} still missing here — make sure you filled ${kit.completion.required_missing === 1 ? "it" : "them"} in the real form.`
            : "."}
        </p>
      </Dialog.Content>
    </Dialog>
  );
}

function Documents({ kit }: { kit: ApplyKitData }) {
  const docs = [
    ...kit.documents.resume_versions.map((d) => ({ ...d, kind: "Resume" })),
    ...kit.documents.cover_letters.map((d) => ({ ...d, kind: "Cover letter" })),
  ];
  if (docs.length === 0) return null;
  return (
    <Card>
      <CardHeader title="Documents to upload" />
      <CardBody>
        <ul className="space-y-2">
          {docs.map((d) => (
            <li key={`${d.kind}-${d.id}`} className="flex items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-2 text-sm text-neutral-200">
                <FileText size={14} aria-hidden className="shrink-0 text-neutral-500" />
                <span className="truncate">{d.label ?? d.kind}</span>
              </span>
              <a href={d.pdf_url} target="_blank" rel="noreferrer">
                <Button
                  variant="ghost"
                  size="sm"
                  leadingIcon={<ExternalLink size={12} aria-hidden />}
                >
                  Open PDF
                </Button>
              </a>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

export default function ApplyKit() {
  const params = useParams<{ applicationId: string }>();
  const applicationId = params.applicationId;
  const kitQuery = useApplyKit(applicationId);
  const kit = kitQuery.data;

  if (!applicationId) {
    return (
      <section className="mx-auto max-w-3xl p-4 sm:p-6 lg:p-8">
        <EmptyState
          tone="danger"
          title="Missing application id"
          description="This page expects /applications/:id/apply-kit."
        />
      </section>
    );
  }

  if (kitQuery.isLoading) {
    return (
      <section className="mx-auto max-w-3xl space-y-3 p-4 sm:p-6 lg:p-8">
        <Skeleton className="h-9 w-1/3" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </section>
    );
  }

  if (kitQuery.error || !kit) {
    return (
      <section className="mx-auto max-w-3xl p-4 sm:p-6 lg:p-8">
        <EmptyState
          tone="danger"
          title="Couldn't load apply kit"
          description="The id may be invalid or you don't have access."
        />
      </section>
    );
  }

  if (!kit.extraction) {
    return (
      <section className="mx-auto max-w-3xl space-y-4 p-4 sm:p-6 lg:p-8">
        <header>
          <h1 className="text-lg font-semibold text-neutral-100">{kit.application.role}</h1>
        </header>
        <EmptyState
          title="No form extracted yet"
          description="Extract the application form in the assistant first, then come back to copy-paste it."
          action={
            <Link to="../apply" relative="path">
              <Button size="sm">Open assistant</Button>
            </Link>
          }
        />
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-3xl space-y-4 p-4 sm:p-6 lg:p-8">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-semibold text-neutral-100">
            {kit.application.role}
          </h1>
          <p className="text-xs text-neutral-500">
            {kit.completion.ready}/{kit.completion.total} fields ready
            {kit.completion.required_missing > 0
              ? ` · ${kit.completion.required_missing} required still missing`
              : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CopyButton value={kit.paste_sheet} label="Copy full paste-sheet" />
          <a href={kit.application.url} target="_blank" rel="noreferrer">
            <Button variant="ghost" size="sm" leadingIcon={<ExternalLink size={12} aria-hidden />}>
              Open posting
            </Button>
          </a>
          <MarkSubmitted kit={kit} />
        </div>
      </header>

      <p className="text-xs text-neutral-500">
        Copy each field into the real application form yourself — nothing here is auto-filled or
        submitted.
      </p>

      <Sections kit={kit} />
      <Documents kit={kit} />
    </section>
  );
}
